import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '@/hooks/useAuth';
import {
  LayoutDashboard, FileText, Users, Boxes, Building2,
  Settings as SettingsIcon, ScrollText, LogOut, Briefcase, UserCircle, Menu, Cpu, Tag,
} from 'lucide-react';
import { cn } from '@/lib/cn';

export function Shell() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [confirmSignOut, setConfirmSignOut] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  if (!user) return <Outlet />;

  const isAdmin = user.role === 'admin';
  const closeDrawer = () => setMobileOpen(false);
  return (
    <div className="min-h-screen min-h-[100dvh] flex">
      {/* Mobile top bar — hamburger + logo. Hidden from md (768px) up. */}
      <header className="md:hidden fixed top-0 left-0 right-0 h-14 bg-ink text-white flex items-center gap-3 px-4 z-40">
        <button
          aria-label="Open menu"
          className="p-1.5 -ml-1.5 rounded hover:bg-white/10"
          onClick={() => setMobileOpen(true)}
        >
          <Menu size={22} />
        </button>
        <img src="/logo-white.webp" alt="RDX" style={{ height: '24px', width: 'auto' }} />
      </header>

      {/* Backdrop behind the mobile drawer. */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/50 z-40"
          onClick={closeDrawer}
          aria-hidden
        />
      )}

      <aside
        className={cn(
          'w-60 bg-ink text-white flex flex-col fixed top-0 left-0 h-screen h-[100dvh] z-50',
          'transition-transform duration-200 md:translate-x-0',
          mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        )}
      >
        <div className="px-5 py-5 border-b border-white/10 flex justify-center">
          <img src="/logo-white.webp" alt="RDX" style={{ height: '28px', width: 'auto' }} />
        </div>

        <nav className="flex-1 py-3 space-y-1 px-2 text-sm overflow-y-auto" onClick={closeDrawer}>
          <Item to="/" icon={<LayoutDashboard size={16} />}>Dashboard</Item>
          <Item to="/pis" icon={<FileText size={16} />}>Proforma Invoices</Item>
          <SectionLabel>Master data</SectionLabel>
          <Item to="/admin/machine-configs" icon={<Cpu size={16} />}>Machine configs</Item>
          {isAdmin && <Item to="/admin/price-config" icon={<Tag size={16} />}>Price config</Item>}
          <Item to="/admin/products" icon={<Boxes size={16} />}>Product catalog</Item>
          <Item to="/customers" icon={<Building2 size={16} />}>Customers</Item>
          <SectionLabel>Configuration</SectionLabel>
          <Item to="/admin/payment-presets" icon={<Briefcase size={16} />}>Payment presets</Item>
          <Item to="/admin/warranty-presets" icon={<ScrollText size={16} />}>Warranty presets</Item>
          <Item to="/admin/settings" icon={<SettingsIcon size={16} />}>Settings</Item>
          {isAdmin && <Item to="/admin/users" icon={<Users size={16} />}>Users</Item>}
          <SectionLabel>Account</SectionLabel>
          <Item to="/account" icon={<UserCircle size={16} />}>My Account</Item>
        </nav>

        <div className="p-3 border-t border-white/10">
          <div className="flex items-center gap-2.5 px-1 py-1.5">
            <div className="w-7 h-7 rounded-full bg-white/15 flex items-center justify-center text-xs font-semibold text-white shrink-0">
              {user.name.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium text-white truncate">{user.name}</div>
              <div className="text-[11px] text-white/50 truncate">{user.email}</div>
            </div>
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/10 text-white/60 capitalize shrink-0">{user.role}</span>
          </div>
          <button
            className="mt-1 w-full flex items-center justify-center gap-2 px-1 py-1.5 text-xs text-white/50 hover:text-white/90 rounded hover:bg-white/5 transition-colors"
            onClick={() => setConfirmSignOut(true)}
          >
            <LogOut size={13} /> Sign out
          </button>
        </div>

        {confirmSignOut && createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
            <div className="bg-white rounded-xl shadow-xl p-6 w-72 space-y-4">
              <div className="text-sm font-semibold text-ink">Sign out?</div>
              <div className="text-xs text-muted">You will be returned to the login screen.</div>
              <div className="flex gap-2 justify-end">
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => setConfirmSignOut(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-sm bg-red-600 hover:bg-red-700 text-white"
                  onClick={async () => { setConfirmSignOut(false); await logout(); nav('/login'); }}
                >
                  Sign out
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
      </aside>

      <div className="flex-1 flex flex-col ml-0 md:ml-60 pt-14 md:pt-0">
        <main className="flex-1 px-4 sm:px-6 py-6 max-w-[1400px] w-full mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function Item({ to, icon, children }: { to: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <NavLink
      to={to}
      end={to === '/'}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-2 rounded px-3 py-2 transition',
          isActive ? 'bg-white/10 text-white' : 'text-white/75 hover:bg-white/5 hover:text-white'
        )
      }
    >
      {icon}
      <span>{children}</span>
    </NavLink>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return <div className="px-3 pt-4 pb-1 text-[10px] uppercase tracking-wide text-white/40">{children}</div>;
}
